/**
 * WebAudio synth. No external assets — everything is generated.
 *
 * Adaptive music bed:
 *   - Stem A: slow pad (triangle dyad → lowpass with slow LFO on cutoff).
 *   - Stem B: fast arpeggio (sawtooth + bandpass + per-note envelope), scheduled at 16th notes.
 *   The two stems crossfade on speedBoost (0 → all A, 1 → all B).
 *   Both stems sum into a `bedDuck` gain that absorbs sidechain ducks.
 * Sparkle:
 *   - 200ms sine glissando layered with chime() on a ring pass, panned with the chime.
 * Sidechain:
 *   - thud() (miss) ducks the bed by 30% for 300ms.
 * Spatial:
 *   - chime() takes a pan parameter (-1..1) sourced from the ring's X offset from the ship.
 * Continuous chime modulation:
 *   - A sustained chime stem (dyad sine pad) lives alongside the bed. Its
 *     output gain is driven by setChimeGain(target, smoothing_ms) — the
 *     canonical "continuous-modulation" API per the Frequency dossier §3.
 *
 * Public API (consumed by game.ts and main.ts — DO NOT break):
 *   arm(), resume(), setMuted(), setVolume(), setDroneIntensity(),
 *   chime(detune, panX), thud()
 * New surface:
 *   setSpeedIntensity(boost) — game.ts feeds ship.speedBoost each frame.
 *   setChimeGain(target, smoothing_ms) — smooth ramp of the sustained
 *     chime stem (Frequency: per-sample inside-band modulation; Pulse v5:
 *     drop's drone lift; Resonance v5: cluster-glow audio).
 */
export class AudioBed {
  muted = false;
  volume = 0.18;
  private ctx?: AudioContext;
  private master?: GainNode;
  private bedDuck?: GainNode;          // sidechain target between stems and master
  private stemA?: GainNode;            // slow pad output
  private stemB?: GainNode;            // arpeggio output
  private bedPresence = 0;             // overall bed level (run state: 0 idle, 1 active, 0.2 paused)
  private speedMix = 0;                // 0 = stem A only, 1 = stem B only
  private arpTimer?: number;
  private arpStep = 0;
  private armed = false;
  // Approach sub-bass: continuous low-freq oscillator gated by proximity to
  // the next ring. Gain ramps via setApproach(intensity).
  private subOsc?: OscillatorNode;
  private subGain?: GainNode;
  // Biome-driven modal shift in semitones. Updated from game.ts as biomes
  // transition; affects the arpeggio notes only (sub-rumble and chimes
  // stay anchored so the harmonic field doesn't drift on every transition).
  private biomeShift = 0;
  // Sustained-chime stem (continuous-modulation API). Two detuned sines
  // through a soft lowpass; gain rides on setChimeGain(). Connects to
  // master (not bedDuck) — it isn't part of the rhythmic bed.
  private chimeGain?: GainNode;

  arm() {
    if (this.armed) return;
    this.armed = true;
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    this.ctx = new Ctx();
    // iOS Safari: AudioContext begins in "suspended" state until a user
    // gesture explicitly resumes it. arm() runs in a click handler, so
    // resume() is permitted here. We re-call it defensively because some
    // iOS versions hand back a context already in "running" — harmless.
    this.ctx.resume?.().catch(() => {});

    this.master = this.ctx.createGain();
    this.master.gain.value = this.muted ? 0 : this.volume;
    this.master.connect(this.ctx.destination);

    // bedDuck sits between stems and master — sidechain modulates it without
    // touching one-shot SFX (chime/thud) which connect to master directly.
    this.bedDuck = this.ctx.createGain();
    this.bedDuck.gain.value = 1;
    this.bedDuck.connect(this.master);

    this.buildStemA();
    this.buildStemB();
    this.buildSubRumble();
    this.buildChimeStem();
  }

  private buildChimeStem() {
    if (!this.ctx || !this.master) return;
    // A5 (880 Hz) + E6 (1318.5 Hz) detuned sines through a soft lowpass.
    // The output gain (chimeGain) is what callers modulate via
    // setChimeGain(). Default 0 — silent until a game asks for presence.
    const o1 = this.ctx.createOscillator();
    const o2 = this.ctx.createOscillator();
    o1.type = "sine";
    o2.type = "sine";
    o1.frequency.value = 880;
    o2.frequency.value = 1318.5;
    o1.detune.value = -4;
    o2.detune.value = +4;
    const lpf = this.ctx.createBiquadFilter();
    lpf.type = "lowpass";
    lpf.frequency.value = 2800;
    lpf.Q.value = 0.6;
    const inner = this.ctx.createGain();
    inner.gain.value = 0.06; // headroom — final taper lives on chimeGain
    o1.connect(lpf);
    o2.connect(lpf);
    lpf.connect(inner);
    const out = this.ctx.createGain();
    out.gain.value = 0;
    inner.connect(out).connect(this.master);
    o1.start();
    o2.start();
    this.chimeGain = out;
  }

  private buildSubRumble() {
    if (!this.ctx || !this.master) return;
    // Continuous sine at 58Hz (sub-bass, just above the lowest pad note).
    // Gated through subGain whose value is driven by setApproach(intensity).
    // Connects to master directly (not bedDuck) so a miss thud doesn't
    // sidechain-suppress the rumble — it's the build-up, not the bed.
    this.subOsc = this.ctx.createOscillator();
    this.subOsc.type = "sine";
    this.subOsc.frequency.value = 58;
    this.subGain = this.ctx.createGain();
    this.subGain.gain.value = 0;
    this.subOsc.connect(this.subGain).connect(this.master);
    this.subOsc.start();
  }

  private buildStemA() {
    if (!this.ctx || !this.bedDuck) return;
    // Detuned triangle dyad through a lowpass whose cutoff is modulated by a
    // slow sine LFO. Produces a breathing pad with no rhythmic content.
    const o1 = this.ctx.createOscillator();
    const o2 = this.ctx.createOscillator();
    o1.type = "triangle";
    o2.type = "triangle";
    o1.frequency.value = 110;             // A2
    o2.frequency.value = 164.81;          // E3 (perfect fifth)
    const lpf = this.ctx.createBiquadFilter();
    lpf.type = "lowpass";
    lpf.frequency.value = 460;
    lpf.Q.value = 0.9;
    // LFO drives filter cutoff ±220Hz around 460Hz.
    const lfo = this.ctx.createOscillator();
    const lfoGain = this.ctx.createGain();
    lfo.type = "sine";
    lfo.frequency.value = 0.07;           // ~14s cycle
    lfoGain.gain.value = 220;
    lfo.connect(lfoGain).connect(lpf.frequency);
    const out = this.ctx.createGain();
    out.gain.value = 0;
    o1.connect(lpf);
    o2.connect(lpf);
    lpf.connect(out);
    out.connect(this.bedDuck);
    o1.start();
    o2.start();
    lfo.start();
    this.stemA = out;
  }

  private buildStemB() {
    if (!this.ctx || !this.bedDuck) return;
    // Per-note envelope scheduler. We don't pre-build oscillators — each
    // 16th-note tick spawns a short osc + bandpass + AR env, then frees it.
    // Cheap (≤7 nodes per tick at 150ms cadence ≈ 47 nodes/sec while active).
    const out = this.ctx.createGain();
    out.gain.value = 0;
    out.connect(this.bedDuck);
    this.stemB = out;

    // A minor 7 arpeggio: A3 C4 E4 G4, octave doubled for shimmer.
    const notes = [220, 261.63, 329.63, 392.0];
    this.arpTimer = window.setInterval(() => {
      if (!this.ctx || !this.stemB) return;
      // Skip work when the stem is effectively silent — saves CPU during slow play.
      if (this.bedPresence * this.speedMix < 0.02) {
        this.arpStep++;
        return;
      }
      const now = this.ctx.currentTime;
      const f = notes[this.arpStep % notes.length];
      const shifted = f * Math.pow(2, this.biomeShift / 12);
      const osc = this.ctx.createOscillator();
      osc.type = "sawtooth";
      osc.frequency.value = shifted * 2;  // octave-up
      const bp = this.ctx.createBiquadFilter();
      bp.type = "bandpass";
      bp.frequency.value = f * 4;
      bp.Q.value = 4;
      const g = this.ctx.createGain();
      g.gain.value = 0;
      g.gain.linearRampToValueAtTime(0.45, now + 0.005);
      g.gain.exponentialRampToValueAtTime(0.0001, now + 0.14);
      osc.connect(bp).connect(g).connect(this.stemB);
      osc.start(now);
      osc.stop(now + 0.16);
      this.arpStep++;
    }, 150);
  }

  /**
   * Overall bed presence — drives both stems' base level. The existing call
   * sites pass 0 (idle/over), 0.2 (paused), 1 (running). API preserved.
   */
  setDroneIntensity(target: number) {
    this.bedPresence = Math.max(0, Math.min(1, target));
    this.applyStemMix();
  }

  /**
   * Speed-driven crossfade between Stem A (pad) and Stem B (arpeggio).
   * Fed `ship.speedBoost` (0..1) every frame from game.ts.
   */
  setSpeedIntensity(boost: number) {
    this.speedMix = Math.max(0, Math.min(1, boost));
    this.applyStemMix();
  }

  /**
   * Biome modal shift in semitones (typically -3..+5). Affects arp notes
   * only, anchored by the static stem-A pad so the harmonic field of the
   * piece doesn't drift on transitions.
   */
  setBiomeShift(semitones: number) {
    this.biomeShift = semitones;
  }

  /**
   * Continuous chime-gain modulation — canonical "continuous-modulation"
   * API per the Frequency dossier §3 sequence diagram.
   *
   * Target is clamped to [0, 1]; smoothing_ms is the linear ramp duration
   * to the new target from the current value. Zero or negative smoothing
   * snaps instantly. Safe to call before arm() (no-op).
   *
   * Example: `audio.setChimeGain(inside ? 1.0 : 0.2, 100);`
   *   — Frequency's 20 Hz sampler ducks the chime to 0.2 when the tuner
   *   falls outside the band, rises back to 1.0 when it re-enters.
   */
  setChimeGain(target: number, smoothing_ms = 100) {
    if (!this.ctx || !this.chimeGain) return;
    const t = Math.max(0, Math.min(1, target));
    const now = this.ctx.currentTime;
    this.chimeGain.gain.cancelScheduledValues(now);
    if (smoothing_ms <= 0) {
      this.chimeGain.gain.setValueAtTime(t, now);
    } else {
      // Anchor the current value so the ramp starts where we are, not
      // where a prior scheduled-value left off.
      this.chimeGain.gain.setValueAtTime(this.chimeGain.gain.value, now);
      this.chimeGain.gain.linearRampToValueAtTime(t, now + smoothing_ms / 1000);
    }
  }

  /**
   * Approach sub-rumble — caller passes 0..1 every frame (game.ts derives
   * it from distance to the nearest oncoming ring). Ramps cheaply via
   * AudioParam to avoid per-frame zipper noise.
   */
  setApproach(intensity: number) {
    if (!this.ctx || !this.subGain) return;
    const i = Math.max(0, Math.min(1, intensity));
    const target = i * 0.18; // peak gain — low enough not to muddy the bed
    const now = this.ctx.currentTime;
    this.subGain.gain.cancelScheduledValues(now);
    this.subGain.gain.linearRampToValueAtTime(target, now + 0.08);
  }

  private applyStemMix() {
    if (!this.ctx || !this.stemA || !this.stemB) return;
    const now = this.ctx.currentTime;
    // Equal-power-ish crossfade; presence is the master scaler.
    const a = this.bedPresence * (1 - this.speedMix) * 0.55;
    const b = this.bedPresence * this.speedMix * 0.45;
    this.stemA.gain.cancelScheduledValues(now);
    this.stemB.gain.cancelScheduledValues(now);
    // Short ramp so per-frame setSpeedIntensity calls don't zipper.
    this.stemA.gain.linearRampToValueAtTime(a, now + 0.1);
    this.stemB.gain.linearRampToValueAtTime(b, now + 0.1);
  }

  setVolume(v: number) {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.master) this.master.gain.value = this.muted ? 0 : this.volume;
  }

  setMuted(m: boolean) {
    this.muted = m;
    if (this.master) this.master.gain.value = m ? 0 : this.volume;
  }

  /** iOS Safari aggressively suspends AudioContext when the tab backgrounds
   *  or the screen locks. Call from a `visibilitychange` handler to recover. */
  resume() {
    if (!this.ctx) return;
    if (this.ctx.state === "suspended") {
      this.ctx.resume().catch(() => {});
    }
  }

  /**
   * Ring-pass chime. `panX` is -1..1; the caller maps the ring's lateral
   * offset from the ship into that range. Falls back to mono if the runtime
   * doesn't expose StereoPannerNode (extremely old browsers).
   */
  chime(detune = 0, panX = 0) {
    if (!this.ctx || !this.master) return;
    const now = this.ctx.currentTime;
    const dest = this.makePannedNode(panX) ?? this.master;
    const base = 880 * Math.pow(2, detune / 12);
    [base, base * 1.5].forEach((f, i) => {
      const osc = this.ctx!.createOscillator();
      const g = this.ctx!.createGain();
      osc.type = "sine";
      osc.frequency.value = f;
      g.gain.value = 0;
      g.gain.linearRampToValueAtTime(0.22 / (i + 1), now + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, now + 0.55);
      osc.connect(g).connect(dest);
      osc.start(now);
      osc.stop(now + 0.6);
    });
    // Sparkle stem — sits on top, same pan as the chime so the spatial cue
    // reads as one event rather than two voices arriving from different sides.
    this.sparkle(panX);
  }

  private sparkle(panX: number) {
    if (!this.ctx || !this.master) return;
    const now = this.ctx.currentTime;
    const dest = this.makePannedNode(panX) ?? this.master;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(1760, now);
    osc.frequency.exponentialRampToValueAtTime(3520, now + 0.2);
    g.gain.value = 0;
    // Tune: 0.12 → 0.18. Sparkle was being masked by the chime's two-osc
    // bell at high streaks; 0.18 keeps the celebratory layer audible without
    // becoming piercing at A6→A7.
    g.gain.linearRampToValueAtTime(0.18, now + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
    osc.connect(g).connect(dest);
    osc.start(now);
    osc.stop(now + 0.24);
  }

  private makePannedNode(panX: number): StereoPannerNode | null {
    if (!this.ctx || !this.master) return null;
    if (typeof this.ctx.createStereoPanner !== "function") return null;
    const pan = this.ctx.createStereoPanner();
    pan.pan.value = Math.max(-1, Math.min(1, panX));
    pan.connect(this.master);
    return pan;
  }

  thud() {
    if (!this.ctx || !this.master) return;
    const now = this.ctx.currentTime;

    // Noise burst
    const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * 0.3, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const bp = this.ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 220;
    bp.Q.value = 0.8;
    const g = this.ctx.createGain();
    g.gain.value = 0.35;
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
    src.connect(bp).connect(g).connect(this.master);
    src.start(now);

    // Sub thump
    const osc = this.ctx.createOscillator();
    const og = this.ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(120, now);
    osc.frequency.exponentialRampToValueAtTime(40, now + 0.25);
    og.gain.value = 0.4;
    og.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
    osc.connect(og).connect(this.master);
    osc.start(now);
    osc.stop(now + 0.32);

    // Sidechain: duck bed 30% for 300ms.
    if (this.bedDuck) {
      this.bedDuck.gain.cancelScheduledValues(now);
      this.bedDuck.gain.setValueAtTime(0.7, now);
      this.bedDuck.gain.linearRampToValueAtTime(1, now + 0.3);
    }
  }
}
